import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Form, InputNumber, Switch, Select } from 'antd';
import { SearchOutlined, PlusOutlined, ThunderboltOutlined, EyeOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import '../Users/Users.css';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const RpcEndpoints: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rpcEndpoints, setRpcEndpoints] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  // Edit functionality moved to detail page
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [providerNameFilter, setProviderNameFilter] = useState<string>(searchParams.get('providerName') || '');
  const [providerNames, setProviderNames] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [pingingIds, setPingingIds] = useState<Set<string>>(new Set());
  const [rpcStatuses, setRpcStatuses] = useState<Map<string, { status: 'online' | 'offline' | 'checking' | 'rate_limited', latency?: number, detail?: string }>>(new Map());
  const [blockchainType, setBlockchainType] = useState<string>('EVM');
  const [blockchains, setBlockchains] = useState<any[]>([]);

  useEffect(() => {
    // Fetch blockchains for blockchainNetworkId dropdown
    const fetchBlockchains = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS, {
          params: { pageId: 1, perPage: 100, status: 'ACTIVE' }
        });
        if (response.data?.status === 'success' && response.data?.data) {
          const data = response.data.data;
          const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
          setBlockchains(items.filter((b: any) => b.status === 'ACTIVE'));
        }
      } catch (error) {
        console.error('Error fetching blockchains:', error);
      }
    };
    fetchBlockchains();

    // Fetch all RPC endpoints to extract unique provider names for the filter dropdown
    const fetchProviderNames = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_RPC_ENDPOINTS, {
          params: { pageId: 1, perPage: 500 }
        });
        if (response.data?.status === 'success' && response.data?.data) {
          const data = response.data.data;
          const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
          const names = items
            .map((item: any) => item.providerName)
            .filter((name: string | null) => name && name.trim() !== '');
          setProviderNames(Array.from(new Set(names)).sort() as string[]);
        }
      } catch (error) {
        console.error('Error fetching provider names:', error);
      }
    };
    fetchProviderNames();
  }, []);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchRpcEndpoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, providerNameFilter, pageId, perPage]);

  const fetchRpcEndpoints = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) {
        params.set('name', search);
      }
      if (statusFilter) {
        params.set('status', statusFilter);
      }
      if (providerNameFilter) {
        params.set('providerName', providerNameFilter);
      }
      params.set('pageId', pageId.toString());
      params.set('perPage', perPage.toString());

      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_GET_RPC_ENDPOINTS}?${params}`);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setRpcEndpoints(items);
        // Extract unique provider names for filter dropdown
        const names = items
          .map((item: any) => item.providerName)
          .filter((name: string | null) => name && name.trim() !== '');
        setProviderNames((prev) => {
          const merged = new Set([...prev, ...names]);
          return Array.from(merged).sort();
        });
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              items.length,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: items.length,
          }));
        }
      }
    } catch (error: any) {
      message.error('Failed to fetch RPC endpoints');
      console.error('Error fetching RPC endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSuggestedPriority = (): number => {
    if (rpcEndpoints.length === 0) {
      return 1;
    }
    // Find the highest priority and suggest the next one
    const priorities = rpcEndpoints
      .map((ep: any) => ep.priority || 0)
      .filter((p: number) => typeof p === 'number');
    
    if (priorities.length === 0) {
      return 1;
    }
    const maxPriority = Math.max(...priorities);
    return maxPriority + 1;
  };

  const handleCreate = () => {
    form.resetFields();
    setBlockchainType('EVM');
    const suggestedPriority = getSuggestedPriority();
    form.setFieldsValue({ priority: suggestedPriority });
    setModalVisible(true);
  };

  // Edit functionality moved to detail page

  const handlePingRpc = async (id: string, rpcUrl: string) => {
    try {
      setPingingIds(prev => new Set(prev).add(id));
      setRpcStatuses(prev => new Map(prev).set(id, { status: 'checking' }));
      
      const startTime = Date.now();
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_PING_RPC_ENDPOINT.replace('{id}', id)
      );
      const latency = Date.now() - startTime;
      
      if (response.data?.status === 'success') {
        setRpcStatuses(prev => new Map(prev).set(id, { status: 'online', latency }));
        message.success(`RPC endpoint is online (${latency}ms)`);
      } else {
        setRpcStatuses(prev => new Map(prev).set(id, { status: 'offline' }));
        message.error('RPC endpoint is offline or unreachable');
      }
    } catch (error: any) {
      // Rate-limited responses mean the RPC IS reachable (our request got a
      // 429 back from the provider), so we MUST NOT mark it as offline — that
      // misleads operators into thinking the node is down. Detect via HTTP
      // status 429 or the backend's `rateLimited: true` detail flag.
      const status = error?.response?.status;
      const details = error?.response?.data?.details;
      const serverMessage: string = error?.response?.data?.message || '';
      const isRateLimited =
        status === 429 ||
        details?.rateLimited === true ||
        /rate[\s-]?limit|too many requests|throttl/i.test(serverMessage);

      if (isRateLimited) {
        setRpcStatuses(prev =>
          new Map(prev).set(id, {
            status: 'rate_limited',
            detail: serverMessage || 'Rate limited',
          })
        );
        message.warning(
          'RPC endpoint is rate limited (reachable, but throttled) — not offline'
        );
      } else {
        setRpcStatuses(prev => new Map(prev).set(id, { status: 'offline' }));
        message.error(serverMessage || 'Failed to ping RPC endpoint');
      }
    } finally {
      setPingingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };


  // Delete functionality moved to detail page

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Remove null/undefined values - Joi validation expects string or absent, not null
      const cleanedValues: Record<string, any> = {};
      for (const [key, value] of Object.entries(values)) {
        if (value !== null && value !== undefined && value !== '') {
          cleanedValues[key] = value;
        }
      }
      
      // Only create, edit is done on detail page
      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_RPC_ENDPOINT, cleanedValues);
      message.success('RPC endpoint created successfully');
      setModalVisible(false);
      fetchRpcEndpoints();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save RPC endpoint');
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Provider',
      dataIndex: 'providerName',
      key: 'providerName',
      width: 150,
      render: (name: string | null) => name || <span style={{ color: COLORS.textSecondary }}>-</span>,
    },
    {
      title: 'RPC URL',
      dataIndex: 'rpcUrl',
      key: 'rpcUrl',
      width: 300,
      render: (url: string) => {
        if (!url) return '-';
        // Show protocol + hostname only (truncate path and query params)
        try {
          const urlObj = new URL(url);
          const displayUrl = urlObj.protocol + '//' + urlObj.hostname;
          return (
            <span title={url} style={{ fontFamily: 'monospace', fontSize: '0.85em', color: COLORS.textPrimary }}>
              {displayUrl}
            </span>
          );
        } catch {
          // If URL parsing fails, show first 40 characters
          const displayUrl = url.length > 40 ? url.substring(0, 40) + '...' : url;
          return (
            <span title={url} style={{ fontFamily: 'monospace', fontSize: '0.85em', color: COLORS.textPrimary }}>
              {displayUrl}
            </span>
          );
        }
      },
    },
    {
      title: 'Type',
      dataIndex: 'blockchainType',
      key: 'blockchainType',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{type || 'EVM'}</Tag>
      ),
    },
    {
      title: 'Network ID',
      dataIndex: 'networkId',
      key: 'networkId',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Availability',
      key: 'availability',
      width: 150,
      render: (_: any, record: any) => {
        const pingStatus = rpcStatuses.get(record.id);
        if (!pingStatus) {
          return <Tag>Not checked</Tag>;
        }
        if (pingStatus.status === 'checking') {
          return <Tag color="processing">Checking...</Tag>;
        }
        if (pingStatus.status === 'online') {
          return <Tag color="success">Online {pingStatus.latency ? `(${pingStatus.latency}ms)` : ''}</Tag>;
        }
        if (pingStatus.status === 'rate_limited') {
          return (
            <Tag color="warning" title={pingStatus.detail || 'Rate limited'}>
              Rate limited
            </Tag>
          );
        }
        return <Tag color="error">Offline</Tag>;
      },
    },
    {
      title: 'Max RPS',
      dataIndex: 'maxRequestsPerSecond',
      key: 'maxRequestsPerSecond',
      width: 100,
      render: (val: number | null) => (
        <span style={{ fontFamily: 'monospace' }}>{val ?? 250}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/kivu/rpc-endpoints/${record.id}`)}
            size="small"
          >
            View
          </Button>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={() => handlePingRpc(record.id, record.rpcUrl)}
            loading={pingingIds.has(record.id)}
            size="small"
            title="Ping RPC endpoint"
          >
            Ping
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>RPC Endpoints</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add RPC Endpoint
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Search</span>
              <Input
                placeholder="Search by name"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onBlur={() => {
                  const newParams = new URLSearchParams(searchParams);
                  if (search) {
                    newParams.set('search', search);
                  } else {
                    newParams.delete('search');
                  }
                  setSearchParams(newParams);
                }}
                style={{ maxWidth: 300 }}
              />
            </div>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Status</span>
              <Select
                placeholder="Filter by status"
                value={statusFilter || undefined}
                onChange={(value) => {
                  setStatusFilter(value);
                  const newParams = new URLSearchParams(searchParams);
                  if (value) {
                    newParams.set('status', value);
                  } else {
                    newParams.delete('status');
                  }
                  setSearchParams(newParams);
                }}
                allowClear
                style={{ width: 150 }}
              >
                <Select.Option value="ACTIVE">Active</Select.Option>
                <Select.Option value="SUSPENDED">Suspended</Select.Option>
                <Select.Option value="PENDING">Pending</Select.Option>
                <Select.Option value="DELETED">Deleted</Select.Option>
                <Select.Option value="PARTIAL">Partial</Select.Option>
              </Select>
            </div>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Provider</span>
              <Select
                placeholder="Filter by provider"
                value={providerNameFilter || undefined}
                onChange={(value) => {
                  setProviderNameFilter(value || '');
                  const newParams = new URLSearchParams(searchParams);
                  if (value) {
                    newParams.set('providerName', value);
                  } else {
                    newParams.delete('providerName');
                  }
                  newParams.set('pageId', '1');
                  setSearchParams(newParams);
                }}
                allowClear
                style={{ width: 180 }}
              >
                {providerNames.map((name) => (
                  <Select.Option key={name} value={name}>{name}</Select.Option>
                ))}
              </Select>
            </div>
          </Space>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ marginBottom: 12, color: COLORS.textSecondary }}>
          Total {pagination.total} RPC endpoints
        </div>
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={rpcEndpoints}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} RPC endpoints
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            className="dark-table"
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="Create RPC Endpoint"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Create"
        width={600}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., INFURA_MAINNET" />
          </Form.Item>
          <Form.Item
            name="providerName"
            label="Provider Name"
          >
            <Input placeholder="e.g., Infura, Alchemy, QuickNode" />
          </Form.Item>
          <Form.Item
            name="blockchainType"
            label="Blockchain Type"
            rules={[{ required: true, message: 'Please select blockchain type' }]}
            initialValue="EVM"
          >
            <Select onChange={(value) => {
              setBlockchainType(value);
              if (value === 'BITCOIN') {
                form.setFieldsValue({ networkId: 0 });
              }
            }}>
              <Select.Option value="EVM">EVM</Select.Option>
              <Select.Option value="BITCOIN">Bitcoin</Select.Option>
              <Select.Option value="SOLANA">Solana</Select.Option>
              <Select.Option value="TRON">TRON</Select.Option>
            </Select>
          </Form.Item>
          {blockchainType === 'BITCOIN' && (
            <Form.Item
              name="networkId"
              label="Network"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch
                checkedChildren="Live (0)"
                unCheckedChildren="Testnet (1)"
                onChange={(checked) => {
                  form.setFieldsValue({ networkId: checked ? 0 : 1 });
                }}
              />
            </Form.Item>
          )}
          <Form.Item
            name="blockchainNetworkId"
            label="Blockchain Network"
            tooltip="Select the blockchain network this RPC endpoint belongs to. Network ID will be auto-filled from the selected blockchain."
            rules={[{ required: true, message: 'Please select a blockchain network' }]}
          >
            <Select
              placeholder="Select blockchain network"
              showSearch
              filterOption={(input, option: any) => {
                if (!option) return false;
                const children = option.children;
                if (!children) return false;
                const childrenStr = Array.isArray(children) 
                  ? children.join('') 
                  : String(children);
                return childrenStr.toLowerCase().includes(input.toLowerCase());
              }}
              onChange={(value) => {
                // Auto-fill networkId from selected blockchain
                const selectedBlockchain = blockchains.find((b: any) => b.id === value);
                if (selectedBlockchain) {
                  // For non-EVM networks, set networkId to 0 if it's null/undefined
                  // But for Bitcoin, use the explicitly selected network (0 or 1) from the form
                  if (blockchainType === 'BITCOIN') {
                    // Keep the current networkId value for Bitcoin (set by the network toggle)
                    return;
                  }
                  const networkId = selectedBlockchain.networkId || (selectedBlockchain.blockchainType !== 'EVM' ? 0 : selectedBlockchain.networkId);
                  form.setFieldsValue({ networkId });
                }
              }}
            >
              {blockchains
                .filter((b: any) => b.blockchainType === blockchainType || (!b.blockchainType && blockchainType === 'EVM'))
                .map((blockchain: any) => (
                  <Select.Option key={blockchain.id} value={blockchain.id}>
                    {blockchain.name} [{blockchain.networkId || 'N/A'}]
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          {/* networkId is auto-filled from the selected blockchain but kept as hidden field for backend */}
          {blockchainType !== 'BITCOIN' && (
            <Form.Item
              name="networkId"
              hidden
            >
              <InputNumber />
            </Form.Item>
          )}
          <Form.Item
            name="rpcUrl"
            label="RPC URL"
            rules={[{ required: true, message: 'Please enter RPC URL' }]}
          >
            <Input
              placeholder="https://..."
              onChange={(e) => {
                const url = e.target.value.toLowerCase();
                if (url.includes('bitcoin') || url.includes('btc')) {
                  form.setFieldsValue({ blockchainType: 'BITCOIN', networkId: 0 });
                  setBlockchainType('BITCOIN');
                } else if (url.includes('solana') || url.includes('sol')) {
                  form.setFieldsValue({ blockchainType: 'SOLANA' });
                  setBlockchainType('SOLANA');
                } else if (url.includes('tron')) {
                  form.setFieldsValue({ blockchainType: 'TRON' });
                  setBlockchainType('TRON');
                }
              }}
            />
          </Form.Item>
          <div style={{ color: '#ff4d4f', fontSize: '14px', marginBottom: '16px', padding: '8px', background: 'rgba(255, 77, 79, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 77, 79, 0.2)' }}>
            ⚠️ Please do not add Public RPC endpoints
          </div>
          {blockchainType === 'BITCOIN' && (
            <>
              <Form.Item
                name="rpcUser"
                label="RPC Username"
                tooltip="Optional: Required only for private Bitcoin RPC endpoints. Public RPCs (e.g., bitcoin-rpc.publicnode.com) don't require authentication."
              >
                <Input placeholder="Bitcoin Core RPC username (optional for public RPCs)" />
              </Form.Item>
              <Form.Item
                name="rpcPassword"
                label="RPC Password"
                tooltip="Optional: Required only if username is provided. Leave empty for public RPC endpoints."
              >
                <Input.Password placeholder="Bitcoin Core RPC password (optional for public RPCs)" />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="priority"
            label="Priority"
            tooltip={`Higher priority RPC endpoints are used first. Currently 
${rpcEndpoints.length > 0 ? `the highest priority in use is ${Math.max(...rpcEndpoints.map((ep: any) => ep.priority || 0))}, 
suggested priority is ${getSuggestedPriority()}` : 'suggested priority is 1'}`}
            rules={[{ required: true, message: 'Please enter priority' }]}
            extra="1 = LIVE (default reader)   •   2 = CATCHUP   •   3 = TRACE (used only for trace_block / debug calls)"
            initialValue={1}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
          </Form.Item>
          <Form.Item
            name="maxRequestsPerSecond"
            label="Max Requests Per Second"
            tooltip="Maximum number of requests per second allowed by this RPC provider. Default: 250. Used to prevent hitting rate limits."
            initialValue={250}
          >
            <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="250" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Form.Item
            name="isDefault"
            label="Is Default"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="SUSPENDED">Suspended</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="allowInOnchainWatcherMonitor"
            label="Allow in OnchainWatcher Monitor"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="supportsRPCPolling"
            label="Supports RPC Polling"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default RpcEndpoints;

